'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { HeaderSearchBar } from '@eliadmin/core/src/components/header/search-bar';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export function Hero({
  categories,
  subtitle,
  title,
  description,
  image,
}: {
  categories: CategoriesEntity[];
  subtitle: string;
  title: string;
  description: string;
  image: string;
}) {
  return (
    <section
      className={cn(
        'custom-section',
        'relative pb-24 pr-0 pt-32 sm:pt-56 md:pr-4 md:pt-56 lg:pt-[301px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-start justify-start gap-24',
        )}
      >
        <div className="flex w-full max-w-[800px] flex-col items-start justify-start gap-6 pr-4 md:pr-0">
          <p className="text-sm font-semibold text-primary sm:text-base">{subtitle}</p>
          <Heading tag="h1" title={title} className={{ title: 'uppercase text-white' }} />
          <Paragraph
            innerHtml={description}
            color="white"
            className="w-full font-medium opacity-80"
            size="2xl"
          />
        </div>
        <div className="flex w-full max-w-[800px] flex-col items-start justify-start gap-3">
          <div className="w-full pr-4 md:pr-0">
            <div className="flex h-14 w-full items-center justify-center rounded-[4px] border border-border bg-white sm:h-16 lg:h-20">
              <HeaderSearchBar />
            </div>
          </div>
          <div className="hide-scroll-bar flex w-full flex-nowrap items-start justify-start gap-3 overflow-x-auto">
            {categories.slice(0, 3).map(category => (
              <RecommendedSearchBadge key={category.id} category={category} />
            ))}
          </div>
        </div>
      </div>
      <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-hero__image-overlay')} />
      <Image
        src={image}
        alt="DULEXIM spol s r.o."
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="100vw"
        loading="eager"
        priority={true}
        fetchPriority="high"
      />
    </section>
  );
}

function RecommendedSearchBadge({ category }: { category: CategoriesEntity }) {
  return (
    <Link
      prefetch={false}
      href={ROUTES.CATEGORY({ slug: category.slug })}
      className={cn(
        'flex h-10 w-fit cursor-pointer items-center justify-center gap-2 rounded-[4px] bg-white/30 px-2.5 transition-all hover:bg-white/50 sm:h-11 sm:px-3 md:px-4 lg:h-12 lg:px-[18px]',
        'ea-recommended-search-badge',
      )}
    >
      <Image
        src={'/images/icons/add-square.svg'}
        alt=""
        width={24}
        height={24}
        className="max-h-6 min-h-6 min-w-6 max-w-6 shrink-0 object-contain"
      />
      <p className="whitespace-nowrap text-xs font-semibold uppercase text-white sm:text-sm">
        {category.name}
      </p>
    </Link>
  );
}
